import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Input,
  Button,
  Alert,
  OtpInput,
  Badge,
  Spinner,
  Card,
  useToast,
} from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { authService } from '@/services/auth.service';
import { kdsService } from '@/services/kds.service';
import type { KdsStation } from '@/services/kds.service';
import { ApiError } from '@/lib/api-client';
import { PlanLockedPage } from '@/components/common/PlanLockedPage';

// ─── Icons ──────────────────────────────────────────────────────────────────

function ChefHatIcon() {
  return (
    <svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 13.87A4 4 0 014 10c0-2.21 1.79-4 4-4 .34 0 .67.04 1 .12A5 5 0 0114 3a5 5 0 015 3.12c.33-.08.66-.12 1-.12a4 4 0 010 8" />
      <path d="M6 13.87V17a2 2 0 002 2h8a2 2 0 002-2v-3.13" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </svg>
  );
}

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'h-6 w-6'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2c1 3-2 5-2 8a4 4 0 108 0c0-3-3-5-2-8" />
      <path d="M12 12c1-1.5 0-3 0-3s-1.5 1.5 0 3z" />
    </svg>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

type Phase = 'otp-request' | 'otp-verify' | 'password' | 'station-select';

// ─── Component ──────────────────────────────────────────────────────────────

export function KdsLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isAuthenticated, verifyOtp, login, setStation } = useAuthStore();

  const [phase, setPhase] = useState<Phase>('otp-request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [otpSentMessage, setOtpSentMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Station selection
  const [stations, setStations] = useState<KdsStation[]>([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  // The business's plan has no kitchen display (API: PLAN_FEATURE_LOCKED).
  const [planLocked, setPlanLocked] = useState(false);
  const [selectedStation, setSelectedStation] = useState<string>(
    searchParams.get('station') ?? '',
  );

  // Redirect URL from auth guard
  const redirectUrl = searchParams.get('redirect');

  // If already authenticated, go to station select
  useEffect(() => {
    if (isAuthenticated && phase !== 'station-select') {
      setPhase('station-select');
      loadStations();
    }
  }, [isAuthenticated]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // ── Load stations ───────────────────────────────────────────────────────
  const loadStations = useCallback(async () => {
    setStationsLoading(true);
    try {
      const data = await kdsService.listStations();
      const activeStations = data.filter((s) => s.is_active);
      setStations(activeStations);

      // Pre-select from URL param
      const preselect = searchParams.get('station');
      if (preselect) {
        const found = activeStations.find((s) => s.id === preselect);
        if (!found) {
          setApiError('Station not found. Contact manager.');
          toast({ variant: 'error', title: 'Station not found', description: 'The pre-selected station was not found. Contact your manager.' });
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PLAN_FEATURE_LOCKED') {
        setPlanLocked(true);
        return;
      }
      setApiError(err instanceof ApiError ? err.message : 'Failed to load stations.');
    } finally {
      setStationsLoading(false);
    }
  }, [searchParams, toast]);

  // ── Auth handlers ─────────────────────────────────────────────────────
  const handleRequestOtp = useCallback(async () => {
    if (!email.trim()) return;
    setIsSubmitting(true);
    setApiError('');
    try {
      await authService.requestOtp(email.trim());
      setOtpSentMessage(`OTP sent to ${email}`);
      setPhase('otp-verify');
      setResendCooldown(60);
      toast({ variant: 'success', title: 'OTP Sent', description: `Check your email at ${email}` });
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Failed to send OTP.');
    } finally {
      setIsSubmitting(false);
    }
  }, [email, toast]);

  const handleVerifyOtp = useCallback(async () => {
    if (otp.length < 6) return;
    setIsSubmitting(true);
    setApiError('');
    try {
      await verifyOtp(email.trim(), otp);
      setPhase('station-select');
      await loadStations();
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Invalid OTP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [email, otp, verifyOtp, loadStations]);

  const handlePasswordLogin = useCallback(async () => {
    if (!email.trim() || !password) return;
    setIsSubmitting(true);
    setApiError('');
    try {
      await login(email.trim(), password);
      setPhase('station-select');
      await loadStations();
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Invalid credentials.');
    } finally {
      setIsSubmitting(false);
    }
  }, [email, password, login, loadStations]);

  const handleEnterKitchen = useCallback(() => {
    if (selectedStation) {
      setStation(selectedStation);
      // Use redirect URL if available, otherwise go to station view
      if (redirectUrl) {
        navigate(redirectUrl);
      } else {
        navigate(`/kds/station/${selectedStation}`);
      }
    } else {
      setStation(null);
      navigate(redirectUrl ?? '/kds');
    }
  }, [selectedStation, setStation, navigate, redirectUrl]);

  // ── Keyboard: Enter to submit ─────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (phase === 'otp-request') handleRequestOtp();
      else if (phase === 'otp-verify') handleVerifyOtp();
      else if (phase === 'password') handlePasswordLogin();
      else if (phase === 'station-select') handleEnterKitchen();
    },
    [phase, handleRequestOtp, handleVerifyOtp, handlePasswordLogin, handleEnterKitchen],
  );

  // ── Render ────────────────────────────────────────────────────────────
  if (planLocked) return <PlanLockedPage />;

  return (
    <div
      className="min-h-screen bg-neutral-950 flex items-center justify-center p-4 animate-fade-in"
      onKeyDown={handleKeyDown}
    >
      {/* Subtle gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-violet-950/30 via-neutral-950 to-pink-950/20 pointer-events-none" />

      <div className="relative w-full max-w-md">
        <Card
          variant="elevated"
          padding="lg"
          className="!bg-neutral-900/80 backdrop-blur-md !shadow-[0_12px_48px_-12px_rgba(139,92,246,0.15)] !rounded-2xl"
        >
          {/* ═══ Logo & Title ═══ */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 text-white mb-4 shadow-lg shadow-violet-500/20">
              <ChefHatIcon />
            </div>
            <h1 className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                Orio360
              </span>
            </h1>
            <p className="text-neutral-400 text-sm mt-1">Kitchen Display System</p>

            {/* Phase indicator */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {(['auth', 'station'] as const).map((step, i) => {
                const isActive = (step === 'auth' && phase !== 'station-select') || (step === 'station' && phase === 'station-select');
                const isDone = step === 'auth' && phase === 'station-select';
                return (
                  <div key={step} className="flex items-center gap-2">
                    {i > 0 && <div className={`w-8 h-px ${isDone || isActive ? 'bg-violet-500' : 'bg-neutral-700'}`} />}
                    <div className={[
                      'w-2 h-2 rounded-full transition-all duration-300',
                      isDone ? 'bg-success-500' : isActive ? 'bg-violet-500 ring-4 ring-violet-500/20' : 'bg-neutral-700',
                    ].join(' ')} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* ═══ Error Banner ═══ */}
          {apiError && (
            <Alert
              variant="danger"
              description={apiError}
              dismissible
              onDismiss={() => setApiError('')}
              className="mb-4"
            />
          )}

          {/* ═══ Phase 1a: OTP Request ═══ */}
          {phase === 'otp-request' && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-1.5">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(val) => setEmail(val)}
                  placeholder="kitchen@restaurant.com"
                  aria-label="Email address"
                />
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={handleRequestOtp}
                loading={isSubmitting}
                disabled={!email.trim()}
                className="w-full"
              >
                Send OTP
              </Button>
              <button
                type="button"
                onClick={() => setPhase('password')}
                className="w-full text-center text-sm text-neutral-500 hover:text-violet-400 transition-colors py-2"
              >
                Use password instead
              </button>
            </div>
          )}

          {/* ═══ Phase 1b: OTP Verify ═══ */}
          {phase === 'otp-verify' && (
            <div className="space-y-4 animate-fade-in">
              {otpSentMessage && (
                <p className="text-sm text-success-400 text-center">{otpSentMessage}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-1.5 text-center">
                  Enter 6-digit OTP
                </label>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  length={6}
                />
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={handleVerifyOtp}
                loading={isSubmitting}
                disabled={otp.length < 6}
                className="w-full"
              >
                Verify & Continue
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setPhase('otp-request'); setOtp(''); setApiError(''); }}
                  className="text-neutral-500 hover:text-neutral-300 transition-colors"
                >
                  &larr; Change email
                </button>
                <button
                  type="button"
                  onClick={handleRequestOtp}
                  disabled={resendCooldown > 0 || isSubmitting}
                  className="text-violet-400 hover:text-violet-300 disabled:text-neutral-600 transition-colors"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                </button>
              </div>
            </div>
          )}

          {/* ═══ Phase 1c: Password Login ═══ */}
          {phase === 'password' && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-1.5">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(val) => setEmail(val)}
                  placeholder="kitchen@restaurant.com"
                  aria-label="Email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-1.5">
                  Password
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(val) => setPassword(val)}
                  placeholder="Enter password"
                  aria-label="Password"
                />
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={handlePasswordLogin}
                loading={isSubmitting}
                disabled={!email.trim() || !password}
                className="w-full"
              >
                Sign In
              </Button>
              <button
                type="button"
                onClick={() => { setPhase('otp-request'); setPassword(''); setApiError(''); }}
                className="w-full text-center text-sm text-neutral-500 hover:text-violet-400 transition-colors py-2"
              >
                Use OTP instead
              </button>
            </div>
          )}

          {/* ═══ Phase 2: Station Selection ═══ */}
          {phase === 'station-select' && (
            <div className="space-y-5 animate-fade-in">
              <div className="text-center">
                <h2 className="text-lg font-bold text-white">Select Station</h2>
                <p className="text-neutral-400 text-sm mt-1">Choose your kitchen station to begin</p>
              </div>

              {stationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner variant="page" size="md" label="Loading stations..." />
                </div>
              ) : stations.length === 0 ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-neutral-800 flex items-center justify-center">
                    <FlameIcon className="h-6 w-6 text-neutral-500" />
                  </div>
                  <p className="text-neutral-400 text-sm">No active stations found.</p>
                  <p className="text-neutral-600 text-xs mt-1">Contact your manager to configure KDS stations.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* All Stations option */}
                  <button
                    type="button"
                    onClick={() => setSelectedStation('')}
                    className={[
                      'w-full flex items-center gap-3 p-4 rounded-xl transition-all duration-200',
                      !selectedStation
                        ? 'bg-violet-500/15 ring-2 ring-violet-500 shadow-lg shadow-violet-500/10'
                        : 'bg-neutral-800/60 hover:bg-neutral-700/40',
                    ].join(' ')}
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white shrink-0 shadow-md shadow-violet-500/20">
                      <FlameIcon className="h-5 w-5" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-bold text-white">All Stations</p>
                      <p className="text-xs text-neutral-400">View orders across all stations</p>
                    </div>
                    {!selectedStation && (
                      <Badge variant="info" size="sm">Selected</Badge>
                    )}
                  </button>

                  {/* Station selector — using StationSelector component */}
                  {stations.length > 0 && (
                    <div className="space-y-2">
                      {stations.map((station) => (
                        <button
                          key={station.id}
                          type="button"
                          onClick={() => setSelectedStation(station.id)}
                          className={[
                            'w-full flex items-center gap-3 p-4 rounded-xl transition-all duration-200',
                            selectedStation === station.id
                              ? 'bg-violet-500/15 ring-2 ring-violet-500 shadow-lg shadow-violet-500/10'
                              : 'bg-neutral-800/60 hover:bg-neutral-700/40',
                          ].join(' ')}
                        >
                          <div className={[
                            'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200',
                            selectedStation === station.id
                              ? 'bg-gradient-to-br from-violet-500 to-pink-500 text-white shadow-md shadow-violet-500/20'
                              : 'bg-neutral-700 text-neutral-400',
                          ].join(' ')}>
                            <FlameIcon className="h-5 w-5" />
                          </div>
                          <div className="text-left flex-1">
                            <p className="text-sm font-bold text-white">{station.name}</p>
                            <p className="text-xs text-neutral-500 uppercase tracking-wider">{station.station_type}</p>
                          </div>
                          {!station.is_online && (
                            <Badge variant="warning" size="sm">Offline</Badge>
                          )}
                          {selectedStation === station.id && (
                            <Badge variant="info" size="sm">Selected</Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                onClick={handleEnterKitchen}
                disabled={stationsLoading}
                className="w-full"
              >
                Enter Kitchen
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
